import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { useTranslation } from 'react-i18next';

export interface PleriModalProps {
  /**
   * Modal contents to render
   */
  children: React.ReactNode;
  /**
   * boolean - Is the Modal open?
   */
  isOpen: boolean;
  /**
   * Title
   */
  title: string;
  /**
   * onClose handler when the close button or backdrop is clicked
   */
  onClose: () => void;
  /**
   * Optional size of the modal, default is 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * Simple PleriModal component for confirmation dialogs and simple modals
 *
 * Example:
 * ```
 *   <PleriModal
 *     isOpen={isOpen}
 *     title="Delete Conversation"
 *     onClose={() => setIsOpen(false)}
 *   >
 *     <p>Are you sure you want to delete this conversation?</p>
 *   </PleriModal>
 * ```
 */
export const PleriModal = ({
  children,
  isOpen,
  title,
  onClose,
  size = 'md',
  className = '',
}: PleriModalProps) => {
  const { t } = useTranslation('pleri/shared');
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const getModalSize = () => {
    switch (size) {
      case 'lg':
        return 'max-w-lg';
      case 'sm':
        return 'max-w-sm';
      default:
        return 'max-w-md';
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[99999999999] flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className={`w-full ${getModalSize()} rounded-lg bg-white shadow-xl dark:bg-gray-900 ${className}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2
            id="modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-gray-100"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-400"
            aria-label={t('closeModal')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
};
